import { Link as RouterLink, useLocation } from "react-router-dom";
import {
    Alert,
    Box,
    Button,
    Collapse,
    IconButton,
    Paper,
    Stack,
    Typography,
} from "@mui/material";
import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { useState } from "react";
import { useSelector } from "react-redux";
import { getPageGuide } from "../../config/adminWorkflow";

/**
 * Short in-page guide: what this screen is for (paper equivalent + steps).
 */
export default function AdminPageGuide() {
    const { pathname } = useLocation();
    const { currentUser } = useSelector((state) => state.user);
    const guide = getPageGuide(pathname, currentUser?.schoolType);
    const [open, setOpen] = useState(true);

    if (!guide || pathname === "/Admin/dashboard" || pathname === "/Admin") {
        return null;
    }

    return (
        <Collapse in={open}>
            <Paper
                variant="outlined"
                sx={{
                    mb: 2,
                    p: { xs: 1.5, sm: 2 },
                    borderRadius: 2,
                    bgcolor: (t) => t.palette.info.main + "08",
                    borderColor: (t) => t.palette.info.main + "40",
                }}
            >
                <Stack direction="row" spacing={1} alignItems="flex-start">
                    <MenuBookOutlinedIcon color="info" sx={{ mt: 0.25, flexShrink: 0 }} />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                            <Box>
                                <Typography variant="subtitle2" fontWeight={800} color="info.dark">
                                    {guide.title}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.25 }}>
                                    Same as on paper: <strong>{guide.paper}</strong>
                                </Typography>
                            </Box>
                            <IconButton size="small" aria-label="Hide guide" onClick={() => setOpen(false)}>
                                <CloseIcon fontSize="small" />
                            </IconButton>
                        </Stack>
                        <Box component="ol" sx={{ m: 0, mt: 1.25, pl: 2.25, "& li": { mb: 0.5 } }}>
                            {guide.steps.map((step) => (
                                <Typography component="li" variant="body2" key={step} color="text.secondary">
                                    {step}
                                </Typography>
                            ))}
                        </Box>
                        {guide.tip ? (
                            <Alert severity="info" icon={<CheckCircleOutlineIcon fontSize="inherit" />} sx={{ mt: 1.5, py: 0.5 }}>
                                <Typography variant="body2">{guide.tip}</Typography>
                            </Alert>
                        ) : null}
                    </Box>
                </Stack>
            </Paper>
        </Collapse>
    );
}

/** First-time setup checklist for the dashboard. */
export function AdminSetupChecklist({ items }) {
    const doneCount = items.filter((i) => i.done).length;
    const allDone = doneCount === items.length;

    return (
        <Paper variant="outlined" sx={{ p: { xs: 2, sm: 2.5 }, mb: 3, borderRadius: 2 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1} sx={{ mb: 1.5 }}>
                <Box>
                    <Typography variant="overline" color="primary.main" fontWeight={800}>
                        First-time setup
                    </Typography>
                    <Typography variant="h6" fontWeight={800}>
                        {allDone ? "You are ready for daily use" : `Setup checklist (${doneCount}/${items.length})`}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        Follow these steps once — the same order as opening new register books in the office.
                    </Typography>
                </Box>
            </Stack>
            <Stack spacing={1}>
                {items.map((item, index) => (
                    <Stack
                        key={item.key || item.to || item.label}
                        direction={{ xs: "column", sm: "row" }}
                        alignItems={{ xs: "stretch", sm: "center" }}
                        justifyContent="space-between"
                        spacing={1}
                        sx={{
                            p: 1.25,
                            borderRadius: 1.5,
                            bgcolor: item.done ? (t) => t.palette.success.main + "10" : "action.hover",
                            border: (t) => `1px solid ${item.done ? t.palette.success.main + "35" : t.palette.divider}`,
                        }}
                    >
                        <Stack direction="row" spacing={1.5} alignItems="center">
                            <Box
                                sx={{
                                    width: 28,
                                    height: 28,
                                    borderRadius: "50%",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    fontWeight: 800,
                                    fontSize: 13,
                                    bgcolor: item.done ? "success.main" : "grey.300",
                                    color: item.done ? "success.contrastText" : "text.primary",
                                }}
                            >
                                {item.done ? <CheckCircleOutlineIcon sx={{ fontSize: 18 }} /> : index + 1}
                            </Box>
                            <Typography variant="body2" fontWeight={item.done ? 600 : 500}>
                                {item.label}
                            </Typography>
                        </Stack>
                        {!item.done ? (
                            <Button
                                component={RouterLink}
                                to={item.to}
                                variant="contained"
                                size="small"
                                sx={{ alignSelf: { xs: "stretch", sm: "center" }, whiteSpace: "nowrap" }}
                            >
                                Open this step
                            </Button>
                        ) : (
                            <Typography variant="caption" color="success.main" fontWeight={700}>
                                Done
                            </Typography>
                        )}
                    </Stack>
                ))}
            </Stack>
        </Paper>
    );
}
